import { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { MODULE_META } from "../constants/modules";
import { TickBox, TickColumnHead, TICK_HINT } from "./NicheTickBox";

/**
 * Triage one module before rebuilding it.
 *
 * Opened from the salmon "!" on a module row in the sidebar, which is where she
 * is when the question occurs to her — she's looking at My Visibility, she knows
 * her Sales Path didn't change, and she wants to say so without opening the niche
 * review and hunting for it.
 *
 * Every stale section in the module, each marked either **Still true** or
 * **Needs redo**. The starting position comes from the tier, so the common case
 * is confirming what Brill already thinks. Nothing is written until she starts
 * the rebuild; closing the dialog changes nothing.
 *
 * "Still true" restamps the section to her current niche and records it as
 * cleared, which is the one thing the walkthrough skips silently. Everything else
 * it stops at, even sections already on the current niche — stepping over those
 * without a word is what made a rebuild feel like it was jumping around.
 *
 * Deliberately not a ConfirmDialog: nothing here is destructive, and its shell is
 * a single line of message text with two buttons.
 */
export function NicheTriageDialog({ module: mod, onClose, onStart }) {
  const meta = MODULE_META[mod.moduleKey] || {};
  const Icon = meta.icon;

  /* Pre-answered from the tier. A red section defaults to "needs redo", an amber
     one to "still true" — amber means niche-flavoured and usually fine, so the
     default should be the answer she'd most often give. She overrides either way,
     and the button is disabled for nothing, so a default can't decide for her. */
  const [marks, setMarks] = useState(() =>
    Object.fromEntries(mod.stale.map((s) => [s.name, s.tier === "redo" ? "redo" : "keep"])));

  const keeps = mod.stale.filter((s) => marks[s.name] === "keep").map((s) => s.name);
  const redos = mod.stale.length - keeps.length;

  /* The boxes and their column headers are shared with the niche review screen —
     same question, same control, so the two screens cannot drift apart. */
  const choice = (sectionName, value, label) => (
    <TickBox
      on={marks[sectionName] === value}
      tone={value}
      label={`${sectionName} — ${label}`}
      hint={TICK_HINT[value]}
      onClick={() => setMarks((prev) => ({ ...prev, [sectionName]: value }))}
    />
  );
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-surface)", borderRadius: 14,
          maxWidth: 480, width: "100%", maxHeight: "84vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 22px 14px", borderBottom: "1px solid var(--border-subtle)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {Icon && <Icon size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />}
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
                {meta.label || mod.moduleKey}
              </h3>
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-secondary)" }}>
              Your niche changed. Mark anything that still holds and Brill will skip it — the rest we go through
              together, from the top.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              flexShrink: 0, background: "none", border: "none", cursor: "pointer",
              padding: 4, display: "flex",
            }}
          >
            <X size={15} color="var(--text-muted)" />
          </button>
        </div>

        {/* The sections */}
        <div style={{ padding: "6px 22px", overflowY: "auto", flex: 1 }}>
          {/* Said once, over the columns, rather than on all eleven buttons. */}
          <div style={{
            display: "flex", alignItems: "flex-end", gap: 6,
            padding: "4px 0 7px", borderBottom: "1px solid var(--border-subtle)",
          }}>
            <span style={{ flex: 1, minWidth: 0 }} />
            <TickColumnHead label="Still true" />
            <TickColumnHead label="Needs redo" />
          </div>
          {mod.stale.map(({ name, tier }) => (
            <div
              key={name}
              style={{
                display: "flex", alignItems: "center",
                gap: 6, padding: "11px 0", borderBottom: "1px solid var(--border-subtle)",
              }}
            >
              <span style={{
                fontSize: 13, minWidth: 0, flex: 1,
                display: "flex", alignItems: "center", gap: 8,
                color: marks[name] === "keep" ? "var(--text-muted)" : "var(--text-primary)",
                textDecoration: marks[name] === "keep" ? "line-through" : "none",
              }}>
                <span style={{
                  flexShrink: 0, width: 6, height: 6, borderRadius: "50%",
                  background: tier === "redo" ? "var(--danger-text)" : "var(--warn-line)",
                }} />
                {name}
              </span>
              {choice(name, "keep", "Still true")}
              {choice(name, "redo", "Needs redo")}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 22px 20px", borderTop: "1px solid var(--border-subtle)" }}>
          <button
            onClick={() => onStart(mod.moduleKey, keeps)}
            style={{
              width: "100%", padding: "11px 15px", borderRadius: 9, border: "none",
              background: "var(--btn-bg)", color: "var(--btn-text)",
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {/* THE BUTTON SAYS WHAT IT DOES. With nothing marked for redo it used
                to read "Walk through it anyway", which describes going back
                through the module and revising — and then `handleRebuildModule`
                found an empty queue and returned, so it did the opposite: it
                filed everything as still true and closed. She read the label and
                expected to be walked through her whole story again (2026-09-01).
                Her decision, not a rewrite of the flow: name the action. */}
            {redos === 0
              ? keeps.length === 1 ? "Keep it as it is" : `Keep all ${keeps.length} as they are`
              : redos === mod.stale.length
                ? "Rebuild from the top"
                : `Start with the other ${redos}`}
            <ArrowRight size={13} />
          </button>
          {/* Walking through anyway is still a real choice, so it stays — quietly,
              underneath, rather than as the thing she is being pushed towards. */}
          {redos === 0 && (
            <button
              onClick={() => onStart(mod.moduleKey, [])}
              style={{
                width: "100%", marginTop: 8, padding: "6px 10px", borderRadius: 8,
                border: "none", background: "none", color: "var(--text-muted)",
                fontSize: 11.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Or walk through it anyway
            </button>
          )}
          <p style={{ margin: "10px 0 0", fontSize: 11.5, lineHeight: 1.6, color: "var(--text-disabled)", textAlign: "center" }}>
            {keeps.length > 0
              ? <>{keeps.length} marked still true — {keeps.length === 1 ? "it stays" : "they stay"} exactly as {keeps.length === 1 ? "it is" : "they are"}, stamped to your current niche.</>
              : <>Nothing is overwritten until you lock it.</>}
            {" "}You can revise any locked section later, whenever you want.
          </p>
        </div>
      </div>
    </div>
  );
}
